import clsx from "clsx";
import { useSnackbar } from "notistack";
import useAuth from "src/hooks/useAuth";
import { useClaims } from "src/hooks/useClaims";
import MyAvatar from "src/components/MyAvatar";
import React, { Fragment, useEffect, useRef, useState } from "react";
import PopoverMenu from "src/components/PopoverMenu";
import useIsMountedRef from "src/hooks/useIsMountedRef";
import { useHistory } from "react-router-dom";
import { alpha, makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Box,
  Divider,
  Typography,
  Tooltip,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import chevronDown from "@iconify-icons/eva/chevron-down-fill";
import swapIcon from "@iconify-icons/eva/swap-outline";
import { MAvatar, MIconButton } from "src/theme";
import ModalEmployee from "./ModalEmployee";
import { useSelector } from "react-redux";
import createAvatar from "src/utils/createAvatar";
import { useDispatch } from "src/redux/store";
import axios from "src/utils/axios";
import { cleanAll } from "src/redux/slices/hub";
import { LoadingButton } from "@mui/lab";
import QRCode from "qrcode";
import { DialogAnimate } from "src/components/Animate";
import { openKiosk, GetAllEmployees } from "src/redux/slices/employee";
import { shouldRefetchEmployees } from "src/utils/employeesBootstrap";

// ----------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  btnAvatar: {
    padding: 0,
    width: 44,
    height: 44,
  },
  isSelected: {
    "&:before": {
      zIndex: 1,
      content: "''",
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      position: "absolute",
      background: alpha(theme.palette.grey[900], 0.8),
    },
  },
  pill: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 0.25, 0.25, 0.5),
    borderRadius: 999,
    // Fondo OPACO (no alpha) para garantizar contraste: si el topbar es
    // claro, paper también — pero al ser opaco no se mezcla con el fondo
    // y el texto siempre se ve. Si el TpvLayoutSettings pinta un fondo
    // custom detrás, esta pill no se transparenta.
    border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.04),
    cursor: "pointer",
    transition: "background-color .15s",
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.16)
          : alpha(theme.palette.common.black, 0.08),
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
  },
  pillName: {
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.1,
    maxWidth: 120,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    // Forzamos el color del texto del tema (no heredado) para que no se
    // pierda cuando un componente padre setea color blanco/transparente.
    color: theme.palette.text.primary,
  },
  pillRole: {
    fontSize: 10,
    lineHeight: 1.1,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  pillMeta: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 0,
  },
  chevron: {
    padding: 2,
    color: theme.palette.text.secondary,
  },
}));

// ----------------------------------------------------------------------

function Account() {
  const classes = useStyles();
  const history = useHistory();
  const anchorRef = useRef(null);
  const isMountedRef = useIsMountedRef();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isOpen, setOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const { employees, currentActiveEmployee } = useSelector(
    (state) => state.employees
  );
  const { user, logout } = useAuth();
  const { isEmployeeWebSession } = useClaims();
  // Cuando la sesión web es PERSONAL de un empleado (login con sus
  // credenciales / QR de empleado), NO permitimos cambiar de usuario
  // desde la propia sesión: rompería la trazabilidad legal (acciones
  // del "otro" quedarían atribuidas al device de esta persona). Si otro
  // quiere operar, debe cerrar sesión y loguearse él, o escanear su QR
  // propio. El patrón "cambiar rápido" queda para TPV compartido cuando
  // el device está logueado como admin dueño (o QR admin).
  const isPersonalEmployeeSession = isEmployeeWebSession;
  const [currentEmployee, setCurrentEmployee] = useState(null);

  const [openQr, setOpenQr] = useState(false);
  const [qr, setQr] = useState(null);

  useEffect(() => {
    const current = employees.find(
      (employee) => employee.id === currentActiveEmployee
    );

    if (current) {
      setCurrentEmployee(current);
      return;
    }

    let admin = employees.find((employee) => employee.isAdmin);
    if (!admin) {
      // Fallback al usuario de la licencia (admin del restaurante). El
      // `user` del JWT tiene `nombreEmpresa`/`firstName`/`lastName`, NO
      // `.name` — sin esta normalización el pill mostraba "Admin" sin
      // nombre. Prioridad: nombreEmpresa (restaurante) → nombre persona.
      const displayName =
        user?.nombreEmpresa?.trim() ||
        [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
        user?.email ||
        "Administrador";
      setCurrentEmployee({
        ...user,
        name: displayName,
        isAdmin: true,
        working: true,
      });
      return;
    }

    setCurrentEmployee(admin);
  }, [currentActiveEmployee]);

  // Lista de empleados perdida tras el login (GET abortado por una recarga):
  // se vuelve a pedir una vez al montar la barra superior.
  useEffect(() => {
    if (shouldRefetchEmployees(employees, user, isPersonalEmployeeSession)) {
      dispatch(GetAllEmployees());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const noHaveEmployees =
    !employees ||
    employees?.length == 0 ||
    employees?.find((u) => u?.isActive) === undefined;

  const handleLogout = async () => {
    setIsLoading(true);

    try {
      const sessionIdentifier =
        window.localStorage.getItem("sessionIdentifier");
      if (
        sessionIdentifier != null &&
        sessionIdentifier != undefined &&
        sessionIdentifier != "null" &&
        sessionIdentifier
      )
        await axios
          .post(`/authentication/closeSession/${sessionIdentifier}`)
          .then(async () => {
            await logout();
            dispatch(cleanAll());
            window.localStorage.clear();

            if (isMountedRef.current) {
              history.push("/");
              handleClose();
            }
          })
          .catch(async (error) => {
            await logout();
            dispatch(cleanAll());
            window.localStorage.clear();

            if (isMountedRef.current) {
              history.push("/");
              handleClose();
            }
          });
      else {
        await logout();
        dispatch(cleanAll());
        window.localStorage.clear();

        if (isMountedRef.current) {
          history.push("/");
          handleClose();
        }
      }
    } catch (error) {
      enqueueSnackbar("Unable to logout", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchEmployee = () => {
    // Bloqueado si el logueado es un empleado personal (login web con
    // email+password). Ver comentario en isPersonalEmployeeSession.
    if (isPersonalEmployeeSession) {
      handleOpen();
      return;
    }
    if (noHaveEmployees) {
      handleOpen();
      return;
    }
    dispatch(openKiosk());
  };

  const renderAvatar = () =>
    currentEmployee?.isAdmin ? (
      <MyAvatar sx={{ width: 32, height: 32 }} />
    ) : (
      <MAvatar
        src={currentEmployee?.name}
        alt={currentEmployee?.name}
        color={createAvatar(currentEmployee?.name).color}
        sx={{ width: 32, height: 32, fontSize: 14 }}
      >
        {createAvatar(currentEmployee?.name).name}
      </MAvatar>
    );

  return (
    <>
      {isMobile ? (
        // Mobile: UN SOLO IconButton 36×36 con avatar dentro. Click abre
        // kiosko directo. Doble-tap o long-press cae al menú clásico vía
        // chevron — para no engordar visualmente el topbar, el chevron
        // queda como mini-overlay en la esquina del propio avatar. Si más
        // adelante se quiere otro acceso al menú clásico, hay que pensar
        // un patrón que no rompa la simetría con los demás iconos.
        <Tooltip title={noHaveEmployees ? "Sin usuarios" : "Cambiar empleado"}>
          <MIconButton
            ref={anchorRef}
            onClick={handleSwitchEmployee}
            // Long press (>500ms) abre el menú; click corto cambia empleado.
            // Cubre el caso "necesito QR/logout" sin sacrificar 1-click.
            onContextMenu={(e) => {
              e.preventDefault();
              handleOpen();
            }}
            className={clsx(classes.btnAvatar, {
              [classes.isSelected]: isOpen,
            })}
            sx={{ position: "relative" }}
          >
            {renderAvatar()}
            <Box
              sx={{
                position: "absolute",
                right: -2,
                bottom: -2,
                width: 14,
                height: 14,
                borderRadius: "50%",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpen();
              }}
            >
              <Icon icon={chevronDown} width={10} height={10} />
            </Box>
          </MIconButton>
        </Tooltip>
      ) : (
        // En desktop: pill con avatar + nombre + chevron. Click en la pill
        // (zona avatar+nombre) abre el kiosko directamente (cambio empleado,
        // 1 click). El chevron mantiene el menú clásico (QR, logout, email).
        <Box className={classes.pill} sx={{ display: "flex" }}>
          <Tooltip title={noHaveEmployees ? "Sin usuarios" : "Cambiar empleado"}>
            <Box
              onClick={handleSwitchEmployee}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pr: 0.5,
                cursor: noHaveEmployees ? "not-allowed" : "pointer",
              }}
            >
              {renderAvatar()}
              <Box className={classes.pillMeta}>
                <Typography className={classes.pillName} title={currentEmployee?.name}>
                  {currentEmployee?.name || "—"}
                </Typography>
                <Typography className={classes.pillRole}>
                  {currentEmployee?.isAdmin ? "Admin" : "Empleado"}
                </Typography>
              </Box>
              <Icon
                icon={swapIcon}
                width={14}
                height={14}
                style={{ opacity: 0.5, marginLeft: 2 }}
              />
            </Box>
          </Tooltip>
          <Tooltip title="Más opciones">
            <IconButton
              ref={anchorRef}
              onClick={handleOpen}
              className={classes.chevron}
              size="small"
              sx={{ ml: 0.25 }}
            >
              <Icon icon={chevronDown} width={16} height={16} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {openQr && (
        <DialogAnimate
          open={openQr}
          onClose={() => {
            setOpenQr(false);
            setQr(null);
          }}
        >
          <Fragment>
            <img src={qr} />
            <Box sx={{ mt: 3, p: 2 }}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={() => {
                  setOpenQr(false);
                  setQr(null);
                }}
              >
                Cerrar
              </Button>
            </Box>
          </Fragment>
        </DialogAnimate>
      )}

      <PopoverMenu
        width={220}
        open={isOpen}
        onClose={handleClose}
        anchorEl={anchorRef.current}
      >
        <Box sx={{ my: 2, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {currentEmployee?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {currentEmployee?.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box
          sx={{ my: 2, px: 2.5 }}
          justifyContent={"center"}
          alignContent={"center"}
        >
          {!isPersonalEmployeeSession && (
            <Button
              variant="invariant"
              disabled={noHaveEmployees}
              onClick={() => {
                dispatch(openKiosk());
                handleClose();
              }}
            >
              <Typography variant="subtitle1" noWrap>
                {noHaveEmployees ? "Sin usuarios" : "Cambiar usuario"}
              </Typography>
            </Button>
          )}

          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => {
              if (
                currentActiveEmployee == null ||
                currentActiveEmployee == undefined ||
                currentActiveEmployee == 0
              ) {
                axios
                  .post(`/Authentication/GenerateQrAccess`)
                  .then((response) => {
                    enqueueSnackbar("Generando código QR...", {
                      variant: "success",
                    });

                    QRCode.toDataURL(
                      response.data,
                      {
                        width: 800,
                        margin: 2,
                      },
                      (err, url) => {
                        setOpenQr(true);
                        setQr(url);
                      }
                    );
                  })
                  .catch((error) => {
                    enqueueSnackbar("Error al generar el QR", {
                      variant: "error",
                    });
                  });
              } else {
                axios
                  .post(`/Employee/GenerateLoginQr/${currentActiveEmployee}`)
                  .then((response) => {
                    enqueueSnackbar("Generando código QR...", {
                      variant: "success",
                    });

                    QRCode.toDataURL(
                      response.data,
                      {
                        width: 800,
                        margin: 2,
                      },
                      (err, url) => {
                        setOpenQr(true);
                        setQr(url);
                      }
                    );
                  })
                  .catch((error) => {
                    enqueueSnackbar("Error al generar el QR", {
                      variant: "error",
                    });
                  });
              }
            }}
          >
            <Typography variant="subtitle1" noWrap>
              Generar QR
            </Typography>
          </Button>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ p: 2, pt: 1.5 }}>
          <LoadingButton
            fullWidth
            color="info"
            variant="contained"
            onClick={handleLogout}
            loading={isLoading}
          >
            Cerrar sesión
          </LoadingButton>
        </Box>
      </PopoverMenu>
    </>
  );
}

export default Account;
